/**
 * Filter Components
 * Components for filtering and multi-select functionality.
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Check, ChevronDown, X } from 'lucide-react';

/**
 * Advanced Multi-Select Component
 * A dropdown with search, select all, and clear all functionality.
 */
export const AdvancedMultiSelect = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = "Select items...", 
  searchPlaceholder = "Search...",
  showSearch = true,
  showSelectAll = true,
  maxDisplayItems = 3,
  className = ""
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    onChange(options.map(option => option.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleToggleItem = (itemValue) => {
    const newValue = value.includes(itemValue)
      ? value.filter(v => v !== itemValue)
      : [...value, itemValue];
    onChange(newValue);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length <= maxDisplayItems) {
      return value.map(v => {
        const option = options.find(opt => opt.value === v);
        return option?.label || v;
      }).join(', ');
    }
    return `${value.length} selected`;
  };

  const getSelectedLabels = () => {
    return value.map(v => {
      const option = options.find(opt => opt.value === v);
      return option?.label || v;
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between text-left font-normal ${className}`}
          title={value.length > maxDisplayItems ? getSelectedLabels().join(', ') : ''}
          data-testid="multi-select-trigger"
        >
          <span className="truncate">{getDisplayText()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          {showSearch && (
            <CommandInput 
              placeholder={searchPlaceholder}
              value={searchTerm}
              onValueChange={setSearchTerm}
              data-testid="multi-select-search"
            />
          )}
          <CommandList className="max-h-64">
            {showSelectAll && (
              <CommandGroup>
                <div className="flex gap-2 p-2 border-b">
                  <Button size="sm" variant="outline" onClick={handleSelectAll} data-testid="select-all-btn">
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleClearAll} data-testid="clear-all-btn">
                    Clear All
                  </Button>
                </div>
              </CommandGroup>
            )}
            
            <CommandEmpty>No items found.</CommandEmpty>
            
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleToggleItem(option.value)}
                  data-testid={`multi-select-option-${option.value}`}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      value.includes(option.value) ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Filter Chips Component
 * Displays active filters as removable badges.
 */
export const FilterChips = ({ filters, filterOptions, onRemoveFilter, onClearAll }) => {
  const getChips = () => {
    const chips = [];

    // Date range chip (only show if not default)
    if (filters.dateRangePreset && filters.dateRangePreset !== 'last_30_days') {
      const presetLabels = {
        'last_7_days': 'Last 7 Days',
        'this_month': 'This Month',
        'last_month': 'Last Month',
        'custom': `${filters.customStartDate} to ${filters.customEndDate}`
      };
      chips.push({
        type: 'dateRange',
        label: presetLabels[filters.dateRangePreset] || filters.dateRangePreset,
        key: 'dateRange'
      });
    }

    // Status chips
    if (filters.statuses?.length > 0) {
      if (filters.statuses.length <= 2) {
        filters.statuses.forEach(status => {
          chips.push({
            type: 'status',
            label: `Status: ${status}`,
            key: `status-${status}`,
            value: status
          });
        });
      } else {
        chips.push({
          type: 'status',
          label: `${filters.statuses.length} statuses`,
          key: 'status-multiple',
          tooltip: filters.statuses.join(', ')
        });
      }
    }

    // Client Product chips
    if (filters.clientProducts?.length > 0) {
      if (filters.clientProducts.length <= 2) {
        filters.clientProducts.forEach(product => {
          chips.push({
            type: 'clientProduct',
            label: `Product: ${product}`,
            key: `product-${product}`,
            value: product
          });
        });
      } else {
        chips.push({
          type: 'clientProduct',
          label: `${filters.clientProducts.length} products`,
          key: 'product-multiple',
          tooltip: filters.clientProducts.join(', ')
        });
      }
    }

    // Assigned To chips
    if (filters.assignedUsers?.length > 0) {
      const userLabels = filters.assignedUsers.map(userId => {
        const user = filterOptions.assignedUsers?.find(u => u.id === userId);
        return user?.name || userId;
      });

      if (userLabels.length <= 2) {
        userLabels.forEach((label, index) => {
          chips.push({
            type: 'assignedUser',
            label: `Assigned: ${label}`,
            key: `user-${filters.assignedUsers[index]}`,
            value: filters.assignedUsers[index]
          });
        });
      } else {
        chips.push({
          type: 'assignedUser',
          label: `${userLabels.length} users`,
          key: 'user-multiple',
          tooltip: userLabels.join(', ')
        });
      }
    }

    // Client Code chips
    if (filters.clientCodes?.length > 0) {
      if (filters.clientCodes.length <= 2) {
        filters.clientCodes.forEach(code => {
          chips.push({
            type: 'clientCode',
            label: `Code: ${code}`,
            key: `code-${code}`,
            value: code
          });
        });
      } else {
        chips.push({
          type: 'clientCode',
          label: `${filters.clientCodes.length} codes`,
          key: 'code-multiple',
          tooltip: filters.clientCodes.join(', ')
        });
      }
    }

    return chips;
  };

  const chips = getChips();

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="filter-chips">
      <span className="text-sm text-slate-600">Active filters:</span>
      {chips.map(chip => (
        <Badge 
          key={chip.key}
          variant="secondary" 
          className="flex items-center gap-1 cursor-pointer hover:bg-slate-200"
          title={chip.tooltip}
          data-testid={`filter-chip-${chip.key}`}
        >
          {chip.label}
          <button
            onClick={() => onRemoveFilter(chip.type, chip.value)}
            className="ml-1 hover:text-red-600"
            aria-label={`Remove ${chip.label} filter`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      {chips.length > 0 && (
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={onClearAll} 
          className="text-xs"
          data-testid="clear-all-filters"
        >
          Clear All
        </Button>
      )}
    </div>
  );
};

export default { AdvancedMultiSelect, FilterChips };
